import React, { useCallback } from 'react';
import { gql } from '@apollo/client';
import { ButtonBarButtonsProp } from '../../ButtonBar';
import { Icon } from '../../../blocks/icons';
import { useBlockingRouter } from '../../../hooks/useBlockingRouter';
import {
	InvoiceStatus,
	useInvoiceEditButtonBarVoidButton_MarkInvoiceVoidMutation,
	UseInvoiceEditButtonBarVoidButtonInvoiceFragment,
} from '#types';
import { useToast } from '../../../hooks/useToast';
import { useDispatch } from 'react-redux';
import { displayConfirm } from '../../../redux/actions';
import { INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID } from '../../../lib/editorBlockerIds';

export const useInvoiceEditButtonBarVoidButtonInvoiceFragment = gql`
	fragment useInvoiceEditButtonBarVoidButtonInvoice on Invoice {
		id
		status
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InvoiceEditButtonBarVoidButton_markInvoiceVoid = gql`
	mutation InvoiceEditButtonBarVoidButton_markInvoiceVoid(
		$where: InvoiceWhereUniqueInput!
	) {
		markInvoiceVoid(where: $where) {
			id
			status
		}
	}
`;

type useInvoiceEditButtonBarSendButtonProps = {
	invoice?: UseInvoiceEditButtonBarVoidButtonInvoiceFragment;
	disabled?: boolean;
};

type useInvoiceEditButtonBarVoidButtonReturnType = {
	button: ButtonBarButtonsProp['buttons'];
};

export const useInvoiceEditButtonBarVoidButton = ( {
	invoice,
	disabled,
}: useInvoiceEditButtonBarSendButtonProps ): useInvoiceEditButtonBarVoidButtonReturnType => {
	const dispatch = useDispatch();
	const router = useBlockingRouter();
	const { showError, showSuccess } = useToast();

	const isVisible =
		invoice?.status &&
		[ InvoiceStatus.Sent, InvoiceStatus.Draft ].includes( invoice.status );
	const [ markInvoiceVoid, { loading } ] =
		useInvoiceEditButtonBarVoidButton_MarkInvoiceVoidMutation();

	const callback = useCallback( async () => {
		dispatch( displayConfirm( { active: false } ) );
		if ( invoice ) {
			const response = await markInvoiceVoid( {
				variables: { where: { id: invoice.id } },
			} );
			if ( response.errors ) {
				response.errors.forEach( ( error ) => showError( error ) );
			} else {
				showSuccess( 'Invoice voided' );
				router.push( '/invoices', {
					overrideBlockers: [ INVOICE_EDIT_UNSAVED_CHANGES_BlOCKER_ID ],
				} );
			}
		}
	}, [
		dispatch,
		invoice,
		markInvoiceVoid,
		router,
		showError,
		showSuccess
	] );

	const onClick = useCallback(
		() =>
			dispatch(
				displayConfirm( {
					active: true,
					props: {
						title: 'Are you sure?',
						message: 'Voiding this invoice cannot be undone.',
						options: [
							{
								text: 'Cancel',
								className: 'button--ghost',
								callback: () => dispatch( displayConfirm( { active: false } ) ),
							},
							{
								text: 'Yes',
								callback,
							},
						],
						defaultCallback: callback,
					},
				} )
			),
		[ callback, dispatch ]
	);

	return {
		button: {
			Void: {
				isVisible,
				color: 'secondary',
				isIconWhenSmall: true,
				'data-cy': 'void-button',
				startIcon: <Icon type='decline' />,
				loading,
				disabled,
				onClick,
			},
		},
	};
};
